// Every circle's session from one call (36-desyr-id.md, Behaviour 6, v1.58.15): the admin service
// gives this browser a chat session in each of the person's circles. New circles become accounts
// with their own databases; sessions are renewed 10 minutes before they end, or when their chat
// server refuses the token, one renewal at a time across tabs. The hops of Behaviour 2 are the fallback.
import type { Config, DesyrIdConfig } from '../core/config';
import { ask, isSigningOut, readSession, writeSession, type Session, type Who } from '../core/session';
import { readJson, writeJson } from '../core/storage';
import { sendSession } from '../core/worker';
import { askUnlock, deleteDatabasesOf, freshDb, keepWaiting, storePrefix, waiting, type Account } from '../accounts/store';
import { deviceName, reach } from '../signin/server';
import { idCall, type Circle, type CirclesAnswer } from './api';
import { startSignIn } from './oidc';
import { authGen } from './store';
import { keepDrafts } from '../chat/drafts';

export type Given = 'ready' | 'kept' | 'hop' | 'passkey' | 'unavailable' | 'ended';
export interface CircleSession {
  state: Given;
  homeserver?: string;
  userId?: string;
  deviceId?: string;
  accessToken?: string;
  expiresAt?: string;
  sessionId?: string;
}
export interface SessionsAnswer extends CirclesAnswer {
  circles: (Circle & { session?: CircleSession })[];
}
/**
 * What a call came to: the answer, the fallback (Behaviour 6.5), an error of the whole call, a token
 * another tab renewed meanwhile, or an answer dropped because a sign-out came in between (4.4.3, v1.58.63).
 */
export type Outcome = { answer: SessionsAnswer } | { fallback: true } | { error: 'unavailable' | 'sign-in' } | { meanwhile: true } | { dropped: true };

const DEVICE = 'desyr_id_device';
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/** "DESYR" and 10 capital letters from the secure random source (each letter equally likely). */
export function makeDevice(): string {
  let letters = '';
  while (letters.length < 10) for (const b of crypto.getRandomValues(new Uint8Array(16))) if (b < 234 && letters.length < 10) letters += LETTERS[b % 26];
  return `DESYR${letters}`;
}
export const fromCall = (deviceId: string) => /^DESYR[A-Z]{10}$/.test(deviceId);
/** The device for circles new on this browser, kept until one has used it (Behaviour 6.2). */
function newDevice(): string {
  const kept = readJson<string>(DEVICE);
  if (kept && fromCall(kept)) return kept;
  const made = makeDevice();
  writeJson(DEVICE, made);
  return made;
}
/** After any sign-out on this device, and once a circle has used it: the next new circle gets a new device. */
export const forgetDevice = () => writeJson(DEVICE, undefined);

/**
 * Naming the call's devices (04-sign-in.md, Device names, v1.58.25, v1.58.26): the call sends no name,
 * so once a session of this browser has answered its first sync, its "DESYR…" device gets the sign-in's
 * name when it has none: a new one, and any made before (v0.2.1's too). Per chat ID and device the
 * tries left (none kept: two, this start's and one more at the next); 0 once done.
 */
const NAMING = 'desyr_device_naming';
const naming = (key: string, tries: number) => writeJson(NAMING, { ...readJson<Record<string, number>>(NAMING), [key]: tries });
const triedNow = new Set<string>();
export async function nameDevice(a: Who & Pick<Session, 'device_id'>): Promise<void> {
  const key = `${a.user_id} ${a.device_id}`;
  const left = readJson<Record<string, number>>(NAMING)?.[key] ?? 2;
  if (!left || !fromCall(a.device_id) || triedNow.has(key)) return;
  triedNow.add(key);
  const path = `/v3/devices/${encodeURIComponent(a.device_id)}`;
  const ok = (r: Response) => (r.ok ? r : Promise.reject(r.status));
  try {
    const { display_name: name } = await ask(a, path).then(ok).then((r) => r.json());
    // A device with a name (the person's Rename, another sign-in's) is never renamed.
    if (!name || name === a.device_id) await ask(a, path, { method: 'PUT', body: JSON.stringify({ display_name: deviceName() }) }).then(ok);
    naming(key, 0);
  } catch {
    naming(key, left - 1);
  }
}

/** Every account on this device still signed in: the open one and the waiting ones. */
const here = (): Account[] => {
  const current = readSession();
  return [...(current ? [{ ...current, db: storePrefix() }] : []), ...waiting().filter((a) => !a.signed_out && a.user_id !== current?.user_id)];
};
/** An account here doesn't know its circle yet (from a hop or the old sign-in): the list names it. */
export const unknownCircles = () => here().some((a) => !a.circle);
/** Less than 10 minutes left (or ended): renewed in the next call. */
export const due = (s: Pick<Session, 'expires_at'>, now = Date.now()) => s.expires_at !== undefined && s.expires_at - now < 10 * 60_000;
/** An account on this device whose session from the call needs renewing before desyr opens (Behaviour 6.1). */
export const anyDue = () => here().some((a) => due(a));

/** An account's stored session changed: the open one also in the worker and in this tab's client. */
function store(a: Account): void {
  const { db, signed_out: _, ...session } = a;
  if (readSession()?.user_id === a.user_id) {
    writeSession(session);
    sendSession();
    tokenChanged?.(session.access_token);
  } else keepWaiting(waiting().map((w) => (w.user_id === a.user_id ? { ...w, ...session } : w)));
}
/** The open circle's client follows a renewed token (set by the client while it runs). */
let tokenChanged: ((token: string) => void) | undefined;
export const followToken = (set?: (token: string) => void) => (tokenChanged = set);

/** Waits for the service's Retry-After (at most a minute). */
const later = (seconds: unknown) => new Promise((r) => setTimeout(r, Math.min(60, Number(seconds) || 5) * 1000));

/** One call (rule 2): the accounts here by circle, those to renew, the device for new circles. */
async function send(id: DesyrIdConfig, devices: Record<string, string>, renew: string[], again = true): Promise<SessionsAnswer | 'fallback' | 'unavailable' | 'sign-in'> {
  try {
    return await idCall<SessionsAnswer>(id, '/me/sessions', { method: 'POST', body: { devices, renew, newDevice: newDevice() } });
  } catch (e) {
    const { status, code, retryAfter } = e as { status?: number; code?: string; retryAfter?: string };
    if (code === 'desyr.id.bad_device' && again) return (forgetDevice(), send(id, devices, renew, false));
    if (status === 429 && again) return (await later(retryAfter), send(id, devices, renew, false));
    if (status === 503 && code === 'desyr.id.unavailable') return 'unavailable';
    if (status === 401) return 'sign-in';
    return 'fallback'; // 404 or 405 (no sessions call), another 5xx, no answer, anything else (rule 5)
  }
}

/** A `ready` session is used only when it is the circle's account, the device sent and the circle's chat server (rule 3). */
async function checked(c: Circle, s: CircleSession, device: string): Promise<Session | undefined> {
  if (!s.accessToken || !s.homeserver || s.userId !== c.mxid || s.deviceId !== device) return undefined;
  const base = await reach(c.server).catch(() => undefined);
  if (!base || base !== s.homeserver.replace(/\/+$/, '')) return undefined;
  return { base_url: base, user_id: c.mxid, device_id: device, access_token: s.accessToken, expires_at: Date.parse(s.expiresAt ?? '') || Date.now() + 3600_000, session_id: s.sessionId, circle: c.slug };
}

/**
 * The call and its answer applied: new circles become waiting accounts, renewed ones get their new
 * token, ended ones are signed out here (the open one finds out from its chat server's next answer).
 * `renew`: the chat IDs to renew besides the due ones; `refused`: renew only if the stored token is
 * still this one (another tab may have renewed it meanwhile). One call at a time across tabs.
 */
export function callSessions(config: Config, opts: { renew?: string[]; refused?: string; slugs?: (mxid: string) => string | undefined } = {}): Promise<Outcome> {
  const run = async (): Promise<Outcome> => {
    const id = config.desyrId!;
    const accounts = here();
    const slugOf = (a: Account) => a.circle ?? opts.slugs?.(a.user_id);
    if (opts.refused && !accounts.some((a) => a.access_token === opts.refused)) return { meanwhile: true };
    const devices = Object.fromEntries(accounts.flatMap((a) => (slugOf(a) ? [[slugOf(a)!, a.device_id]] : [])));
    const renew = accounts.filter((a) => slugOf(a) && a.expires_at !== undefined && (due(a) || opts.renew?.includes(a.user_id))).map((a) => slugOf(a)!);
    const device = newDevice();
    const gen = authGen();
    const answer = await send(id, devices, renew);
    // Signed out meanwhile (in any tab): no session is stored (Behaviour 4.4.3, v1.58.63).
    if (gen !== authGen()) return { dropped: true };
    if (answer === 'fallback') return { fallback: true };
    if (answer === 'unavailable' || answer === 'sign-in') return { error: answer };
    let used = false;
    for (const c of answer.circles) {
      const s = c.session;
      const mine = accounts.find((a) => a.user_id === c.mxid);
      if (s?.state === 'ready') {
        const session = await checked(c, s, devices[c.slug] ?? device);
        if (gen !== authGen()) return { dropped: true };
        if (!session) c.session = { state: 'unavailable' };
        else if (mine && devices[c.slug]) store({ ...mine, ...session });
        else if (mine) continue; // an account here whose circle wasn't known: its own device and databases stay
        else {
          used ||= !devices[c.slug];
          // A circle signed out here (ended elsewhere) comes back as a new one: its old databases go.
          const before = waiting().find((w) => w.user_id === c.mxid);
          if (before) void deleteDatabasesOf(before.db).catch(() => {});
          keepWaiting([...waiting().filter((w) => w !== before && w.user_id !== c.mxid), { ...session, db: freshDb(session.device_id) }]);
          askUnlock(c.mxid); // a new device: "Unlock your earlier messages" may ask once (19, v1.58.10)
        }
      } else if (s?.state === 'ended' && mine) {
        forgetDevice();
        if (readSession()?.user_id !== mine.user_id) keepWaiting(waiting().map((w) => (w.user_id === mine.user_id ? { ...w, signed_out: true } : w)));
      }
      // An account from a hop or the old sign-in learns its circle, for the next call's `devices`.
      if (mine && !mine.circle && s?.state !== 'ended') store({ ...(here().find((a) => a.user_id === c.mxid) ?? mine), circle: c.slug });
    }
    if (used) forgetDevice();
    return { answer };
  };
  return navigator.locks ? navigator.locks.request('desyr_id_sessions', run) : run();
}

/** The stored token of an account here. */
const tokenOf = (userId: string) => here().find((a) => a.user_id === userId)?.access_token;

/**
 * A token its chat server refused (`M_UNKNOWN_TOKEN`, rule 4): renewed, or taken from the tab that
 * renewed it meanwhile. The new one; "ended" when the session was ended elsewhere or the circle has
 * none from the call; "later" when desyr ID or the service can't renew it now: nobody is signed out
 * because desyr ID is down (Behaviour 1.4, v1.58.16), the next turn tries again. "sign-in" when desyr
 * ID's own session is over: the refresh token refused, or a second `desyr.id.sign_in` right after a
 * renewal (a sign-in from before a recovery's cutoff, Behaviour 1.4 and 7.2.6, v1.58.57).
 */
export async function renewRefused(config: Config, userId: string, refused: string): Promise<string | 'ended' | 'later' | 'sign-in'> {
  const outcome: Outcome = tokenOf(userId) === refused ? await callSessions(config, { renew: [userId], refused }) : { meanwhile: true };
  const token = tokenOf(userId);
  if (token && token !== refused) {
    if (readSession()?.user_id === userId) tokenChanged?.(token);
    return token;
  }
  if ('error' in outcome && outcome.error === 'sign-in') return 'sign-in';
  const given = 'answer' in outcome ? outcome.answer.circles.find((c) => c.mxid === userId)?.session?.state : undefined;
  return 'answer' in outcome && given !== 'unavailable' ? 'ended' : 'later';
}

/** Armed by a refused renewal until the person's next action (or a failed start of the sign-in). */
let armed = false;

/** What would call a server when pressed: a link, a button, a menu item, an option or tab (36, v1.58.68). */
const ACTS = 'a[href], button, [role="button"], [role="link"], [role="menuitem"], [role="menuitemradio"], [role="option"], [role="tab"]';

/**
 * desyr ID's own session is over and the open circle's session can't go on without it: nothing leaves
 * the page by itself, the person stays where they are, reading or typing. Their next action — a press or
 * Enter on what would call a server (send, a room or page, a circle, a menu item, a dialog button; never
 * typing, scrolling, hover, focus or the page shown again) — goes to desyr ID's page in this window instead
 * of running, once, and comes back here; the drafts are kept, a message pressed to send waits unsent in
 * its box (Behaviour 1.4, v1.58.57, v1.58.66, v1.58.68). Nothing after a sign-out meanwhile.
 */
export function signInAtNextAction(config: Config): void {
  if (armed) return;
  armed = true;
  const gen = authGen();
  const act = (e: Event) => {
    const at = e.target instanceof Element ? e.target : null;
    if (e instanceof KeyboardEvent ? e.key !== 'Enter' || e.isComposing || !at?.closest(`${ACTS}, [contenteditable="true"], input, textarea`) : !at?.closest(ACTS)) return;
    removeEventListener('click', act, true);
    removeEventListener('keydown', act, true);
    if (gen !== authGen() || isSigningOut()) return void (armed = false);
    e.preventDefault();
    e.stopPropagation();
    keepDrafts();
    void startSignIn(config.desyrId!, location.pathname + location.search).catch(() => (armed = false));
  };
  addEventListener('click', act, true);
  addEventListener('keydown', act, true);
}

/**
 * The open circle's requests (a session from the call): one refused for its token is sent once more
 * with the renewed one. A burst of refused requests waits on one renewal; once the session has ended,
 * its token is refused here without asking anyone again (36, Behaviour 6.4; 39, Behaviour 2.2). When
 * desyr ID's own session is over, the client sees an outage, never a sign-out, and desyr ID's sign-in
 * waits for the person's next action (Behaviour 1.4, v1.58.66): a renewal nobody asked for never jumps.
 */
export const renewingFetch = (config: Config, userId: string): typeof fetch => {
  const renewals = new Map<string, Promise<string | 'ended' | 'later' | 'sign-in'>>();
  const ended = new Set<string>();
  const refused = () => new Response(JSON.stringify({ errcode: 'M_UNKNOWN_TOKEN', error: 'ended' }), { status: 401, headers: { 'Content-Type': 'application/json' } });
  return async (input, init) => {
    const sent = new Headers(init?.headers).get('Authorization')?.replace(/^Bearer /, '');
    if (sent && ended.has(sent)) return refused();
    const answer = await fetch(input, init);
    if (answer.status !== 401 || !sent || isSigningOut() || (await answer.clone().json().catch(() => ({}))).errcode !== 'M_UNKNOWN_TOKEN') return answer;
    if (!renewals.has(sent)) renewals.set(sent, renewRefused(config, userId, sent));
    const token = await renewals.get(sent)!;
    if (token === 'ended') return (ended.add(sent), answer);
    if (token === 'sign-in') signInAtNextAction(config);
    // Not renewable right now: an outage to the client (it waits and tries again), never a sign-out.
    const outage = new Response(JSON.stringify({ errcode: 'M_UNKNOWN', error: 'unavailable' }), { status: 503, headers: { 'Content-Type': 'application/json' } });
    if (token === 'sign-in') return outage;
    if (token === 'later') return (renewals.delete(sent), outage);
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  };
};

/** Another tab renewed the open circle's session: this tab's client takes the new token from the stored session. */
export function followOtherTabs(userId: string): () => void {
  const changed = (e: StorageEvent) => {
    const s = e.key === 'desyr_session' ? readSession() : undefined;
    if (s?.user_id === userId) tokenChanged?.(s.access_token);
  };
  addEventListener('storage', changed);
  return () => removeEventListener('storage', changed);
}
